'use client';
import { useRef, useState } from 'react';
import { Icon, Typography } from '@workspace/ui/common';

type Media = {
  src: string;
};

type Slide = {
  headline: string;
  description: string;
  subheadline: string;
  subdescription: string;
  media: Media;
};

type VideoSlideCarouselProps = {
  block: {
    slides: Slide[];
  };
};

const NAV_DIRECTION = {
  PREV: 'prev',
  NEXT: 'next',
} as const;

type INAVI_DIRECTION = (typeof NAV_DIRECTION)[keyof typeof NAV_DIRECTION];

export function VideoSlideCarousel(props: VideoSlideCarouselProps) {
  const { slides } = props.block;
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handleNav = (dir: INAVI_DIRECTION) => {
    setCurrentIndex((prev) => {
      if (dir === NAV_DIRECTION.PREV) return (prev - 1 + slides.length) % slides.length;
      return (prev + 1) % slides.length;
    });

    // Pause video when navigating
    if (isPlaying) {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  };
  const handlePrevious = () => handleNav(NAV_DIRECTION.PREV);
  const handleNext = () => handleNav(NAV_DIRECTION.NEXT);

  const currentSlide = slides?.[currentIndex];

  if (!currentSlide) return null;

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const renderVideoPlayer = () => {
    return (
      <video
        ref={videoRef}
        src={currentSlide.media.src}
        className="absolute inset-0 w-full h-full object-cover"
        muted
        onClick={togglePlay}
      />
    );
  };
  return (
    <div className="flex flex-col md:flex-row md:gap-[131px] gap-4 items-center px-6 py-12 md:p-30 bg-accent">
      <div className="flex flex-col gap-8 md:gap-16">
        <div className="flex flex-col gap-2 max-w-[464px]">
          <Typography variant="h3" className="font-bold">
            {currentSlide.headline}
          </Typography>
          <Typography variant="bodyMd" className="">
            {currentSlide.description}
          </Typography>
        </div>
        <div>
          <div className="flex flex-col md:gap-8">
            <div className="flex flex-col gap-2 md:gap-4">
              {/* Counter */}
              <Typography variant="h5" className="flex items-start gap-1 text-primary">
                <Typography variant="h5" className="text-4xl font-bold ">
                  {currentIndex + 1}
                </Typography>
                <Typography variant="h5" className="text-base font-medium">
                  of {slides.length}
                </Typography>
              </Typography>
              <div className="flex flex-col gap-1 md:gap-2">
                <Typography variant="h5" className="">
                  {currentSlide.subheadline}
                </Typography>
                <Typography variant="bodyMd" className="">
                  {currentSlide.subdescription}
                </Typography>
              </div>
            </div>

            {/* Arrows */}
            <div className="flex gap-4 ">
              <Icon name="ChevronLeft" size={40} onClick={handlePrevious} className="text-primary" />
              <Icon name="ChevronRight" size={40} onClick={handleNext} className="text-primary" />
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE: Media */}
      <div className="relative md:max-w-[544px] max-h-[327px] md:max-h-[360px] w-full h-full aspect-video flex items-center justify-center bg-light-silver">
        {renderVideoPlayer()}
        {/* Center play button */}
        {!isPlaying && (
          <div className="p-1 absolute inset-0 flex items-center justify-center z-10">
            <Icon name="PlayCircle" onClick={togglePlay} size={40} />
          </div>
        )}
      </div>
    </div>
  );
}
